/**
 * Live "boxed in" status for one robot (see utils/boxedIn.js): the robot's own
 * nav_progress timer plus the ACTION_NAV_STUCK / ACTION_NAV_UNSTUCK help request.
 * Ticks once a second while active so countdowns move between frames.
 */
import { useEffect, useReducer, useState } from "react";
import { getSocket, subscribeToRobot } from "../services/socketService";
import {
  boxedInReducer,
  describeBoxedIn,
  initialBoxedInState,
  isBoxedInActive,
} from "../utils/boxedIn";

export function useBoxedIn(serialID) {
  const [state, dispatch] = useReducer(boxedInReducer, initialBoxedInState);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    dispatch({ type: "reset" });
    if (!serialID) return undefined;

    const socket = getSocket();
    // Re-run on every connect: room membership is per-connection. Not
    // unsubscribed on cleanup — rooms on the shared socket are not ref-counted,
    // and other hooks (usePatrolStatus) may still need this one. The pull asks
    // the relay for an open help request, so a page opened mid-incident still
    // shows the alert.
    const join = () => {
      subscribeToRobot(serialID);
      socket.emit("frontend:get-ACTION_NAV_STUCK", { robotId: serialID });
    };
    socket.on("connect", join);
    if (socket.connected) join();

    // Untagged events are accepted, matching usePatrolStatus.
    const matches = (data) => !data?.serialID || String(data.serialID) === String(serialID);
    const on = (type) => (data) => {
      if (matches(data)) dispatch({ type, data, now: Date.now() });
    };
    const onNavProgress = on("nav_progress");
    const onStuck = on("stuck");
    const onUnstuck = on("unstuck");
    const onPulledStuck = (reply) => {
      const alert = reply?.alertACTION_NAV_STUCK;
      if (alert && String(reply.robotId) === String(serialID)) {
        dispatch({ type: "stuck", data: { ...alert, pulled: true }, now: Date.now() });
      }
    };

    socket.on("nav_progress", onNavProgress);
    socket.on("ACTION_NAV_STUCK", onStuck);
    socket.on("ACTION_NAV_UNSTUCK", onUnstuck);
    socket.on("get_ACTION_NAV_STUCK", onPulledStuck);
    return () => {
      socket.off("connect", join);
      socket.off("nav_progress", onNavProgress);
      socket.off("ACTION_NAV_STUCK", onStuck);
      socket.off("ACTION_NAV_UNSTUCK", onUnstuck);
      socket.off("get_ACTION_NAV_STUCK", onPulledStuck);
    };
  }, [serialID]);

  const active = isBoxedInActive(state);
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  return { state, view: describeBoxedIn(state, now) };
}

export default useBoxedIn;
